import { Box } from '@material-ui/core'
import { GetProfileResponseDto } from '@shared/core/users'
import classNames from 'classnames'
import { formatInTimeZone } from 'date-fns-tz'
import Tooltip from 'rc-tooltip'
import { ChangeEvent, MouseEvent, useCallback, useMemo, useState } from 'react'
import { NestedValue } from 'react-hook-form'
import { useCalendars } from 'src/modules/calendar/hooks/use-calendars'
import { confirmationVariants } from 'src/modules/confirmation/constants/confirmation-variants'
import { FormattedText, Icon, ToggleSwitch, Typography } from 'src/modules/core/components'
import { useDisplayTimezone } from 'src/modules/core/context'
import focusAwareComponent from 'src/modules/core/utils/focus-aware-component'
import { InboundSmsEventBadgeConnected } from 'src/modules/inbound-notifications/components/inbound-sms-event-badge-connected'
import { ReviewTimingIssuesLogic } from 'src/modules/reminder/components/review-timing-issues-modal/review-timing-issues-logic'
import { useProfile } from 'src/modules/user/hooks/use-profile'
import { EventPreview, ExtendEventPreview } from '../../types/event.types'
import { EventAutoActivationHelper } from '../../utils/EventAutoActivationHelper'
import { useWasEventRecentlyActivated } from '../../utils/recently-activated-events'
import { EventCardPhoneFields } from './event-card-phone-fields'
import { EventCardStatus } from './event-card-status'
import classes from './event-card.module.scss'

export type EventCardVariant = 'finished' | 'current'
type OnChangeHandler = (e: ChangeEvent<HTMLInputElement>) => void

export type EventCardProps = {
  onSwitchChange?: OnChangeHandler
  variant: EventCardVariant
  event: ExtendEventPreview
  eventRepresentsReminder?: boolean
  showBadge?: boolean
  showSwitch?: boolean
  showStatus?: boolean
  showPhoneInputs?: boolean
  isLoading?: boolean
  hasFocus?: boolean
  onCardClick?: (event: ExtendEventPreview, phoneNumbers: ReceiverNumber[]) => void
}

export type ReceiverNumber = {
  id: string
  phone: string
  country: string
}

export type EditEventForm = {
  dummy?: string
  receiverNumbers: NestedValue<ReceiverNumber[]>
}

const offsetBottom = -20

export const EventCard = focusAwareComponent<EventCardProps>(
  ({
    variant,
    event,
    eventRepresentsReminder = false,
    showBadge = true,
    showSwitch = true,
    showStatus = true,
    showPhoneInputs = true,
    onSwitchChange,
    isLoading,
    hasFocus,
    onCardClick,
  }) => {
    const profile = useProfile()
    const calendars = useCalendars()
    const { timezone } = useDisplayTimezone()
    const [currentPhoneNumbers, setCurrentPhoneNumbers] = useState(null)
    const recentlyActivated = useWasEventRecentlyActivated(event.id)

    const eventDate = useMemo(() => new Date(Number(event.event_date)), [event.event_date])
    const originalEventDate = useMemo(
      () => new Date(Number(event.original_event_date ?? event.event_date)),
      [event.original_event_date, event.event_date],
    )

    const isPastEvent = useMemo(() => new Date() > eventDate, [eventDate])
    const isTooLateForReminders = useMemo(() => isTooLateToSendReminders(profile.data, event), [profile.data, event])
    const hasPhoneNumber = useMemo(() => event.receiver_numbers?.length > 0, [event.receiver_numbers])
    const hideSwitch = useMemo(
      () => !showSwitch || isPastEvent || !hasPhoneNumber || (isTooLateForReminders && !event.active),
      [showSwitch, isPastEvent, hasPhoneNumber, isTooLateForReminders, event.active],
    )

    const acceptanceVariant = useMemo(() => {
      if (!showBadge || !event.active || event.confirmed === null || (event.confirmed === 'pending' && isPastEvent)) {
        return null
      }

      if (event.confirmed === 'pending') {
        return confirmationVariants.waiting
      }

      if (event.confirmed === 'confirmed') {
        return confirmationVariants.accept
      }

      if (event.confirmed === 'cancelled') {
        return confirmationVariants.reject
      }

      return null
    }, [showBadge, event.active, event.confirmed, isPastEvent])

    const parentCalendar = useMemo(
      () => calendars.data?.find((calendar) => calendar.id === event.calendar),
      [calendars.data, event.calendar],
    )
    const hasAutoActivationMaterial = EventAutoActivationHelper.hasAutoActivationMaterial(event)

    const handleCardClick = useCallback(() => {
      if (onCardClick) {
        onCardClick(event, currentPhoneNumbers)
      }
    }, [onCardClick, event, currentPhoneNumbers])

    return (
      <div
        className={classNames(classes.root, {
          [classes.clickable]: variant === 'current',
          [classes.autoActivated]: hasAutoActivationMaterial,
        })}
        onClick={handleCardClick}
      >
        {acceptanceVariant && (
          <Tooltip
            placement="top"
            mouseEnterDelay={0.1}
            motion={{ motionName: 'example' }}
            align={{ offset: [0, offsetBottom] }}
            overlayClassName={classes.tooltipOverlay}
            overlay={
              <Typography variant="body" className={classes.bodyText}>
                {acceptanceVariant === confirmationVariants.accept && (
                  <FormattedText id="confirmation.tooltip.accept" />
                )}
                {acceptanceVariant === confirmationVariants.waiting && (
                  <FormattedText id="confirmation.tooltip.pending" />
                )}
                {acceptanceVariant === confirmationVariants.reject && (
                  <FormattedText id="confirmation.tooltip.cancel" />
                )}
              </Typography>
            }
          >
            <div
              className={classNames(classes.statusCircle, {
                [classes.statusCircleAccept]: acceptanceVariant === confirmationVariants.accept,
                [classes.statusCircleWaiting]: acceptanceVariant === confirmationVariants.waiting,
                [classes.statusCircleRejected]: acceptanceVariant === confirmationVariants.reject,
              })}
            >
              {acceptanceVariant === confirmationVariants.accept && <Icon name="markAccepted" />}
              {acceptanceVariant === confirmationVariants.waiting && <Icon name="markWaiting" />}
              {acceptanceVariant === confirmationVariants.reject && <Icon name="markRejected" />}
            </div>
          </Tooltip>
        )}
        <div className={classes.statusAndSwitchContainer}>
          {showStatus && (
            <div className={classes.statusContainer}>
              <EventCardStatus event={event} eventDateIsReminderDate={eventRepresentsReminder} />
              {event.has_unread_inbound_sms ? (
                <InboundSmsEventBadgeConnected eventId={event.id} previewFromEvent={event.unread_inbound_sms_preview} />
              ) : null}
              {event.is_booked_appointment && (
                <div title="Booked appointment">
                  <Icon name="calendarAlt" className={classes.bookingIcon} />
                </div>
              )}
            </div>
          )}
          {!hideSwitch && (
            <div className={classes.flexWrapper}>
              {isLoading && (
                <div className={classes.spinnerFlexWrapper}>
                  <div className={classes.spinnerWrapper}>
                    <div className={classes.spinner}>
                      <div />
                    </div>
                  </div>
                </div>
              )}
              <div className={classes.toggleWrapper} onClick={(e: MouseEvent) => e.stopPropagation()}>
                {variant === 'current' ? (
                  <ToggleSwitch
                    checked={event.active}
                    onChange={onSwitchChange}
                    readOnly={!onSwitchChange || (isPastEvent && !event.active)}
                    disabled={isLoading}
                    variant="primary"
                  />
                ) : (
                  <Icon name="successCheckmark" />
                )}
              </div>
            </div>
          )}
        </div>
        <div>
          <div className={classes.titleWrapper}>
            <div className={classes.circle} style={parentCalendar ? { backgroundColor: parentCalendar.color } : {}} />
            <p className={classes.title} title={event.title}>
              {event.title || <FormattedText id="reminder.noTitle" />}
            </p>
            <input
              type="hidden"
              value={JSON.stringify({
                id: event.id,
                event_id: event.event_id,
                recurring_event_id: event.recurring_event_id,
                google_event_id: event.google_event_id,
              }).replaceAll('"', `'`)}
            />
          </div>
        </div>
        <div className={classes.flexWrapper}>
          <Typography variant="body" className={classes.regular}>
            {eventRepresentsReminder ? 'Reminder sent at ' : ''}
            {event.all_day ? (
              <FormattedText id="reminder.allDay" />
            ) : (
              formatInTimeZone(originalEventDate, timezone, 'h:mm a')
            )}
          </Typography>
        </div>

        {showPhoneInputs &&
          profile.data.usage_mode === 'customer' &&
          !event.active &&
          !recentlyActivated &&
          !isPastEvent && (
            <EventCardPhoneFields
              profile={profile.data}
              event={event}
              hasFocus={hasFocus}
              onPhoneNumbersChange={setCurrentPhoneNumbers}
            />
          )}

        {hasFocus && event.errors.length > 0 && event.active && (
          <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gridGap={3} marginTop={1}>
            <Icon name="eventCardError" />
            <span className={classes.error}>{event.errors.join(', ')}</span>
          </Box>
        )}
      </div>
    )
  },
)

function isTooLateToSendReminders(profile: GetProfileResponseDto, event: EventPreview) {
  if (!profile || !event) return true
  const eventDate = new Date(Number(event.event_date))
  const result = ReviewTimingIssuesLogic.evaluate({
    confirmation_enabled: event.confirmation_enabled,
    confirmation_offsets: event.confirmation_offsets,
    reminder_enabled: event.reminder_enabled,
    reminder_offsets: event.reminder_offsets,
    event_date: eventDate,
  })
  return !result.allClear
}
